import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);


import {
  React,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  useTheme,
  FormControlLabel,
  colors
} from '@mui/material';
import LoadingOverlay from 'react-loading-overlay-nextgen';

LoadingOverlay.propTypes = undefined
import { transpose, downloadCSV } from 'src/utils/miscTools';
import InfoModal from 'src/components/InfoModal';
import InfoPopupContent from 'src/__mocks__/InfoPopupContent';
import {days_performance_url, raw_data_url} from 'src/utils/constants';

function getData(token, deviceId, date, range, trackingOnly, setDaysincident, setDayscollected, setDaysreadingtimes, setIsLoading) {
  setIsLoading(true);

  const post_body = JSON.stringify({
      DATE: date,
      CUMULATIVE: false,
      DISH_IDS: deviceId,
      RANGE: range,
      TRACKING_ONLY: trackingOnly
    })
  fetch(days_performance_url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Token '+ token
    },
    body:post_body
  }).then((response) => {
    if (response.ok) return response.json();
    throw response;
  }).then((json) => {
    if (json.length > 0) {
      const x = transpose(json);
      setDaysreadingtimes(x[0]);
      //console.log(x);
      setDaysincident(x[1]);
      setDayscollected(x[2]);
      //console.log('Days performance query completed with data');
      setIsLoading(false);
    } else {
      console.log('Days performance query completed without data');
      setIsLoading(false);
      setDaysreadingtimes([]);
      setDaysincident([]);
      setDayscollected([]);
      //console.log(json);
    }
  }).catch((err) => {
    console.error(err);
  });
}

const DaysPerformance = (props) => {
  const { date, deviceId, token } = props;
  const [range, setRange] = useState('Peak Hours');
  const [trackingDataOnly, setTrackingDataOnly] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [daysincident, setDaysincident] = useState([]);
  const [dayscollected, setDayscollected] = useState([]);
  const [daysreadingtimes, setDaysreadingtimes] = useState([]);

  function onRangeChange(selectedRange) {
    setRange(selectedRange);
    getData( token, deviceId, date, selectedRange,trackingDataOnly, setDaysincident, setDayscollected, setDaysreadingtimes, setIsLoading);
  }

  function onTrackingOnlyChange() {
    const changedValue = !trackingDataOnly;
    setTrackingDataOnly(changedValue);
    getData( token, deviceId, date, range,changedValue,  setDaysincident, setDayscollected, setDaysreadingtimes, setIsLoading);
  }

  function onDownloadClicked(){
    console.log("retreiving raw data");
    const post_body = JSON.stringify({
      DATE: date,
      CUMULATIVE: false,
      DISH_IDS: deviceId,
      RANGE: range,
      TRACKING_ONLY: trackingDataOnly
    })
    fetch(raw_data_url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Token '+ token
    },
    body:post_body
  }).then((response) => {
    if (response.ok) return response.json();
    throw response;
  }).then((json) => {
    if (json.length > 0) {
      downloadCSV(json.join("\n"),date);
    } else {
      alert('No data for selected parameters');
    }
  }).catch((err) => {
    console.error(err);
  });


    
  }

  useEffect(() => {
    // console.log(deviceId);
    if (deviceId.length > 0 && token){
      getData(token, deviceId, date, range,trackingDataOnly,  setDaysincident, setDayscollected, setDaysreadingtimes, setIsLoading);
    }
  }, [date, deviceId]);

  const theme = useTheme();

  const times = [];

  for (const dateString of daysreadingtimes) {
    const timePart = dateString.split('T')[1];
    times.push(timePart);
  }

  const incidentEnergyDataset = {
    labels: times,
    label: 'Incident Energy',
    pointRadius: 0,
    borderColor: colors.blue[500],
    borderWidth: 1,
    data: daysincident.map((x) => Number(x).toFixed(2))
  };

  const enthalpyChangeDataset = {
    labels: times,
    label: 'Energy Collected',
    pointRadius: 0,
    borderColor: colors.red[500],
    borderWidth: 1,
    data: dayscollected.map((x) => Number(x).toFixed(2))
  };



  const data = {
    datasets: [
      incidentEnergyDataset,
      enthalpyChangeDataset
    ],
    labels: times
  };

  const options = {
    responsive: true,
    layout: { padding: 0 },
    maintainAspectRatio: false,
    
  plugins: {
    legend: { display: false },
  }
};

  const titleText = "Day's Performance (Kilo-Watts)";
  const title = (
    <>
      {titleText}
      <InfoModal content={InfoPopupContent.PERFORMANCE} />
    </>
  );

  return (
    <Card>
      <CardHeader
        action={(
          <div>
            <FormControl component="fieldset">
             <FormControlLabel
                label="Tracking Data Only"
                control={
                  <Checkbox
                    defaultChecked 
                    onChange={onTrackingOnlyChange}
                  />
                }
            />
            </FormControl>
            <Button variant="contained"
              onClick={() => {
                onDownloadClicked();
              }}
            >
              Download Raw Data
            </Button>
            <br/>
            <FormControl component="fieldset">
              <FormLabel component="legend">Range</FormLabel>
              <RadioGroup
                row
                aria-label="range"
                name="row-radio-buttons-group"
                onChange={(e) => onRangeChange(e.target.value)}
              >
                <FormControlLabel value="Morn-Eve" control={<Radio checked={range === 'Morn-Eve'} />} label="Morn-Eve" />
                <FormControlLabel value="Peak Hours" control={<Radio checked={range === 'Peak Hours'} />} label="Peak Hours" />
                <FormControlLabel value="24 Hours" control={<Radio checked={range === '24 Hours'} />} label="24 Hours" />
              </RadioGroup>
            </FormControl>
          </div>
        )}
        title={title}
      />

      <Divider />
      <CardContent>
        <Box
          sx={{
            height: 500,
            position: 'relative'
          }}
        >
          <LoadingOverlay
            active={isLoading}
            spinner
            text="Loading Data..."
            
            styles={{
              wrapper: (base) => ({
               height: 500
              })
            }}
          >
            <Line
              data={data}
              options={options}
            />
          </LoadingOverlay>
        
        </Box>
      </CardContent>
      <Divider />
    </Card>
  );
};

DaysPerformance.propTypes = {
  date: PropTypes.object,
  token: PropTypes.string,
  deviceId: PropTypes.array,
};

export default DaysPerformance;
